/**
 * Video Review Page
 * Page for reviewing and approving/rejecting generated showcase videos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Row,
  Col,
  Button,
  Progress,
  Modal,
  Input,
  message,
  Spin,
  Empty,
  Tag,
  Typography,
  Space,
  Tooltip,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { OnboardingService, VideoForReview, VideoReviewStats } from '../services/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import './VideoReviewPage.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

const VideoReviewPage: React.FC = () => {
  const { productCode } = useParams<{ productCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandId = user?.brandId;

  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<VideoForReview[]>([]);
  const [stats, setStats] = useState<VideoReviewStats | null>(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);

  const loadVideos = useCallback(async () => {
    if (!brandId || !productCode) return;

    try {
      setLoading(true);
      const data = await OnboardingService.getVideosForReview(brandId, productCode);
      setVideos(data.videos);
      setStats(data.stats);
    } catch (error) {
      message.error('Videolar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [brandId, productCode]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  useEffect(() => {
    if (!loading && stats && stats.pendingReview === 0 && stats.pending === 0 && stats.completed > 0) {
      const allApproved = videos.every(v => v.approvalStatus === 'approved' || v.generationStatus !== 'completed');
      if (allApproved && stats.approved === stats.completed) {
        message.success('Tüm videolar onaylandı! Dashboard\'a yönlendiriliyorsunuz...');
        setTimeout(() => navigate('/'), 2000);
      }
    }
  }, [loading, stats, videos, navigate]);

  const handleApprove = async (videoId: string) => {
    if (!brandId || !productCode) return;

    try {
      setProcessing(videoId);
      await OnboardingService.approveVideo(brandId, productCode, videoId);
      message.success('Video onaylandı');
      await loadVideos();
    } catch (error) {
      message.error('Video onaylanamadı');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (videoId: string) => {
    setSelectedVideoId(videoId);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!brandId || !productCode || !selectedVideoId) return;

    try {
      setProcessing(selectedVideoId);
      await OnboardingService.rejectVideo(brandId, productCode, selectedVideoId, rejectReason);
      message.success('Video reddedildi ve yeniden oluşturuluyor');
      setRejectModalVisible(false);
      await loadVideos();
    } catch (error) {
      message.error('Video reddedilemedi');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveAll = async () => {
    if (!brandId || !productCode) return;

    try {
      setProcessing('all');
      const count = await OnboardingService.approveAllVideos(brandId, productCode);
      message.success(`${count} video onaylandı`);
      await loadVideos();
    } catch (error) {
      message.error('Videolar onaylanamadı');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusTag = (video: VideoForReview) => {
    if (video.generationStatus === 'pending' || video.generationStatus === 'processing') {
      return <Tag color="blue">Oluşturuluyor...</Tag>;
    }
    if (video.generationStatus === 'failed') {
      return <Tag color="red">Başarısız</Tag>;
    }
    if (video.approvalStatus === 'approved') {
      return <Tag color="green">Onaylandı</Tag>;
    }
    if (video.approvalStatus === 'rejected') {
      return <Tag color="orange">Reddedildi</Tag>;
    }
    return <Tag color="purple">İnceleme Bekliyor</Tag>;
  };

  const getProductName = () => {
    switch (productCode) {
      case 'fashsnap':
        return 'FashSnap';
      case 'fashplay':
        return 'FashPlay';
      default:
        return productCode;
    }
  };

  if (loading) {
    return (
      <Layout style={{ padding: '24px' }}>
        <Content>
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Videolar yükleniyor...</p>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="video-review-page">
      <Content style={{ padding: '24px' }}>
        <div className="page-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            style={{ marginBottom: 16 }}
          >
            Dashboard'a Dön
          </Button>

          <Title level={2}>{getProductName()} Video İnceleme</Title>
          <Text type="secondary">
            Oluşturulan videoları inceleyin ve onaylayın. Onaylanan videolar bekleme ekranında gösterilecektir.
          </Text>
        </div>

        {stats && (
          <Card className="stats-card" style={{ marginTop: 24, marginBottom: 24 }}>
            <Row gutter={24} align="middle">
              <Col flex="auto">
                <Space size="large">
                  <div>
                    <Text type="secondary">Toplam</Text>
                    <Title level={4} style={{ margin: 0 }}>{stats.total}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Tamamlanan</Text>
                    <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{stats.completed}</Title>
                  </div>
                  <div>
                    <Text type="secondary">İnceleme Bekleyen</Text>
                    <Title level={4} style={{ margin: 0, color: '#722ed1' }}>{stats.pendingReview}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Onaylanan</Text>
                    <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{stats.approved}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Bekleyen</Text>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{stats.pending}</Title>
                  </div>
                </Space>
              </Col>
              <Col>
                <Progress
                  type="circle"
                  percent={Math.round((stats.approved / stats.total) * 100)}
                  format={() => `${stats.approved}/${stats.total}`}
                  size={80}
                />
              </Col>
              <Col>
                {stats.pendingReview > 0 && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleApproveAll}
                    loading={processing === 'all'}
                    size="large"
                  >
                    Tümünü Onayla ({stats.pendingReview})
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        )}

        {stats && stats.pending > 0 && (
          <Alert
            message="Videolar Oluşturuluyor"
            description={`${stats.pending} video hala oluşturuluyor. Sayfa otomatik olarak güncellenecektir.`}
            type="info"
            showIcon
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={loadVideos}>
                Yenile
              </Button>
            }
            style={{ marginBottom: 24 }}
          />
        )}

        {videos.length === 0 ? (
          <Empty description="Henüz video oluşturulmamış" />
        ) : (
          <Row gutter={[24, 24]}>
            {videos.map((video) => (
              <Col xs={24} sm={12} md={8} lg={6} key={video.id}>
                <Card
                  className={`video-card ${video.approvalStatus}`}
                  cover={
                    <div className="video-cover">
                      {video.generatedVideoUrl ? (
                        <div
                          className="video-thumbnail"
                          onClick={() => setPreviewVideo(video.generatedVideoUrl || null)}
                        >
                          <img
                            src={video.productImage || video.generatedImageUrl}
                            alt={video.productName}
                          />
                          <div className="play-overlay">
                            <PlayCircleOutlined />
                          </div>
                        </div>
                      ) : video.generatedImageUrl ? (
                        <img
                          src={video.generatedImageUrl}
                          alt={video.productName}
                        />
                      ) : (
                        <div className="placeholder">
                          {video.generationStatus === 'failed' ? (
                            <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                          ) : (
                            <Spin />
                          )}
                        </div>
                      )}
                    </div>
                  }
                  actions={
                    video.generationStatus === 'completed' && video.approvalStatus === 'pending_review'
                      ? [
                          <Tooltip title="Onayla" key="approve">
                            <Button
                              type="text"
                              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                              onClick={() => handleApprove(video.id)}
                              loading={processing === video.id}
                            />
                          </Tooltip>,
                          <Tooltip title="Reddet" key="reject">
                            <Button
                              type="text"
                              icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                              onClick={() => handleRejectClick(video.id)}
                              loading={processing === video.id}
                            />
                          </Tooltip>,
                        ]
                      : undefined
                  }
                >
                  <Card.Meta
                    title={
                      <Space>
                        <span>{video.productName}</span>
                        {getStatusTag(video)}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary">Model: {video.aiModelName}</Text>
                        {video.regenerationCount > 0 && (
                          <Text type="warning">
                            <ReloadOutlined /> {video.regenerationCount}x yeniden oluşturuldu
                          </Text>
                        )}
                        {video.generationError && (
                          <Text type="danger">{video.generationError}</Text>
                        )}
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          title="Videoyu Reddet"
          open={rejectModalVisible}
          onCancel={() => setRejectModalVisible(false)}
          onOk={handleRejectConfirm}
          okText="Reddet ve Yeniden Oluştur"
          okButtonProps={{ danger: true, loading: processing === selectedVideoId }}
          cancelText="İptal"
        >
          <p>Bu videoyu reddetmek istediğinize emin misiniz? Video yeniden oluşturulacaktır.</p>
          <TextArea
            placeholder="Reddetme sebebi (opsiyonel)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
        </Modal>

        <Modal
          title="Video Önizleme"
          open={!!previewVideo}
          onCancel={() => setPreviewVideo(null)}
          footer={null}
          width={800}
          centered
        >
          {previewVideo && (
            <video
              src={previewVideo}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh' }}
            />
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default VideoReviewPage;
